<template>
  <div class="submit-form">
    <div class="submit-form__container">
      <form @submit.prevent="onFormSubmit">
        <div
          class="submit-form__forms flx flx--col bg--color-01 bor--radius-12 sha--03 p-24"
        >
          <h3
            class="submit-form__title txt--bold txt--color-04 txt--26px txt--height-34px"
          >
            {{ $t('listing.submit') }}
          </h3>
          <div class="submit-form__inputs">
            <exc-input
              v-model="$v.formFields.website.$model"
              :field="$v.formFields.website"
              :label="$t('listing.website')"
              size="s"
              :optional-errors="mergeCustomErrors('website')"
              placeholder="https://example"
              name="website"
              class="submit-form__input"
            />
            <exc-input
              v-model="$v.formFields.email.$model"
              :field="$v.formFields.email"
              :label="$t('listing.email')"
              placeholder="example@gmail.com"
              size="s"
              :optional-errors="mergeCustomErrors('email')"
              name="email"
              class="submit-form__input"
            />
            <exc-input
              v-model="$v.formFields.telegram.$model"
              :field="$v.formFields.telegram"
              :label="$t('listing.telegram')"
              size="s"
              :optional-errors="mergeCustomErrors('telegram')"
              placeholder="@example"
              name="telegram"
              class="submit-form__input"
            />
            <exc-input
              v-model="$v.formFields.comment.$model"
              :field="$v.formFields.comment"
              :label="$t('listing.comment')"
              size="s"
              :optional-errors="mergeCustomErrors('comment')"
              name="comment"
              class="submit-form__input"
            />
          </div>

          <i18n
            path="common.notify.personalDataIsProcessed"
            class="notification-cookies__text txt txt--14px txt--height-18px txt--color-04"
            tag="span"
          >
            <template #url>
              <exc-link
                :to="localePath('/privacy-notice')"
                target="_blank"
                class="txt txt--color-04 txt--bold txt--underline"
                v-text="$t('common.notify.privacyNotice')"
              />
            </template>
          </i18n>

          <exc-button
            :loading="loading || localLoading"
            :disabled="$v.$invalid"
            type="submit"
            :size="isMobile ? 's' : 'm'"
            :full-width="isMobile"
          >
            <span class="txt txt--18px txt--weight-600 txt--height-21px">
              {{ $t('listing.getOffer') }}
            </span>
          </exc-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
// import { mapFieldsToComputed } from "@/store/lib/vuex-form";
import { ExcButton, ExcInput, ExcLink } from '@/components/common'
import AuthMixinCaptcha from '@/mixins/auth/AuthMixinCaptcha'
import AuthFormSubmit from '@/mixins/auth/AuthFormSubmit'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { notifyError, notifySuccess } from '@/lib/bus'

const isUrl = value =>
  /^(?:(?:https?|ftp):\/\/)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00A1-\uFFFF0-9]-*)*[a-z\u00A1-\uFFFF0-9]+)(?:\.(?:[a-z\u00A1-\uFFFF0-9]-*)*[a-z\u00A1-\uFFFF0-9]+)*(?:\.(?:[a-z\u00A1-\uFFFF]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    value,
  )

export default {
  name: 'SubmitForm',
  components: {
    ExcButton,
    ExcLink,
    ExcInput,
  },
  mixins: [AuthMixinCaptcha, AuthFormSubmit, validationMixin, serverFormMixin],
  data () {
    return {
      externalFormLoaded: false,
      localLoading: false, // for mixin
      formFields: {
        website: '',
        bitcoinTalkLink: '',
        email: '',
        comment: '',
        telegram: '',
      },
      loading: false,
    }
  },
  computed: {
    ...mapState({
      isDark: state => state.darktheme.enabled,
    }),
    isMobile () {
      return this.$mq?.resize && this.$mq?.below(768)
    },
    pictureType () {
      return this.isDark ? 'dark' : 'light'
    },
    customErrors () {
      return {
        required: this.$t('validators.required'),
        url: this.$t('validators.url'),
        maxLength255: this.$t('validators.maxLength', { max: 255 }),
        maxLength2000: this.$t('validators.maxLength', { max: 2000 }),
        email: this.$t('validators.email'),
      }
    },
  },
  methods: {
    async submit () {
      const body = {
        ...this.formFields,
        ...this.mix_captchaData,
      }

      const { errors } = await this.$api.applyToList({ body })

      if (errors) {
        notifyError({
          duration: 15000,
          title: this.$t('common.notify.uhOh'),
          text: this.$t('common.notify.somethingWentWrong'),
        })

        return
      }

      notifySuccess({
        title: this.$t('common.notify.applyToList.title'),
        text: this.$t('common.notify.applyToList.text'),
      })
    },
    mergeCustomErrors (fieldName) {
      return {
        ...this.mix_getServerError(fieldName),
        ...this.customErrors,
      }
    },
  },
  validations () {
    return this.mix_validationsServerWrapper({
      formFields: {
        website: {
          required,
          maxLength255: maxLength(255),
          url: isUrl,
        },
        bitcoinTalkLink: {},
        email: {
          required,
          maxLength255: maxLength(255),
          email,
        },
        comment: {
          required,
          maxLength2000: maxLength(2000),
        },
        telegram: {
          maxLength255: maxLength(255),
        },
      },
      mix_captchaData: {},
    })
  },
}
</script>

<style lang="scss" scoped>
.submit-form {
  &__forms {
    gap: toRem(24px);

    @include bp(m) {
      align-items: flex-start;
    }
  }

  &__inputs {
    display: grid;
    width: 100%;
    gap: toRem(20px);

    @include bp(m) {
      grid-template-rows: repeat(4, 1fr);
    }

    @include bp(l) {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    }
  }
}
</style>
